import React, {
    FormEvent,
    ReactElement,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Form } from "react-bootstrap";
import { BiExpandAlt, BiRefresh } from "react-icons/bi";
import {
    ActionContextType,
    FormField, FormFieldDescriptorInput,
    FormFieldType,
    FormFieldVisibility,
    FormType, GetFormDataDocument, GetFormQuery, GetWebhookExampleDocument, GetWebhookExampleQuery, ListRequestTypesDocument,
    Workflow,
    WorkflowStep,
    useCreateWebhookUrlMutation,
    useDeleteFormMutation,
    useGetFormDataQuery,
    useGetFormQuery,
    useSaveFormMutation, useSaveWorkflowMutation,
} from "../../../../__generated__/api";
import client, { removeTypename } from "../../../../api";
import { Loading } from "../../../../components";
import CodeBlock from "../../../../components/code-block/CodeBlock";
import Confirm from "../../../../components/confirm/Confirm";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";
import ExpandingSection from "../../../../components/expanding-section/ExpandingSection";
import { LoadingIcon } from "../../../../components/loading/Loading";
import PageActions from "../../../../components/page-actions/PageActions";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import WorkflowDisplay, { createWorklowStep } from "../../../../components/workflow/Workflow";
import {
    SYSTEM_FIELDS,
} from "../../../../components/workflow/WorkflowFields";
import useArticleContext from "../../../../contexts/HelpContext";
import useRouting, { useParams } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/session";
import createId from "../../../../create-id";
import { ErrorRemote } from "../../../error/Error";

const ACTIONS_CREATED_ID = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:"
    + "(urn:fq:workflow-service:system,webhook),actions)";

const CREATED_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,webhook)";

const ACTION_REQUEST_CREATED = {
    id: "",
    name: "Webhook Invoked",
    actionId: CREATED_ID,
    action: {
        id: "",
        name: "Webhook Called",
        description: "Request first created",
        allowedChildren: [
            {
                id: ACTIONS_CREATED_ID,
                label: "Actions",
                description: "",
            },
        ],
    },
    serviceId: "urn:fq:workflow-service:system",
    description: "The automations to execute after the user has created the request. This allows "
        + "you to take actions such as firing off a web hook.",
    parameters: [],
};

function createEmpty() {
    const workflow = [];
    workflow.push(ACTION_REQUEST_CREATED);
    return workflow;
}

interface Example {
    body: string | URLSearchParams | Record<string, unknown>;
    headers: Record<string, unknown>;
    ipAddress: string;
    method: string;
}

class Stopper {
    isStopped = false;
}

function getSampleForm(stepId: string, stopper: Stopper) {
    return new Promise<string>((resolve, reject) => {
        const start = () => {
            if (stopper.isStopped) {
                return;
            }
            setTimeout(async () => {
                const { data, error } = await client.query<GetWebhookExampleQuery>({
                    query: GetWebhookExampleDocument,
                    variables: {
                        stepId,
                    },
                    fetchPolicy: "no-cache",
                });

                if (error) {
                    reject(error);
                } else if (data?.workflowServicePollWebhookExample?.isFound) {
                    resolve(data.workflowServicePollWebhookExample.formId);
                } else {
                    start();
                }
            }, 10000);
        };
        start();
    });
}

interface JsonItem {
    path: string[];
    value: unknown;
}
function flattenJson(body: Record<string, unknown> | unknown, path: string[], list: JsonItem[]) {
    if (body instanceof URLSearchParams) {
        body.forEach((value, key) => {
            list.push({
                path: [...path, key],
                value,
            });
        });
    } else if (Array.isArray(body)) {
        body.forEach((row, index) => {
            flattenJson(row, [...path, index.toString()], list);
        });
    } else if (body && typeof body === "object") {
        Object.entries(body).forEach(([key, value]) => {
            flattenJson(value, [...path, key], list);
        });
    } else {
        list.push({
            path,
            value: body,
        });
    }
}

interface ParsedBodySample {
    headerNames: string[];
    bodyNames: string[];
    example: Example;
    body: JsonItem[];
}
function parse(sample: Example): ParsedBodySample {
    const copy = sample;
    const contentType = sample.headers["content-type"] as string;
    if (contentType?.includes("application/json")) {
        copy.body = JSON.parse(sample.body as string);
    } else if (contentType?.includes("application/x-www-form-urlencoded")) {
        copy.body = new URLSearchParams(sample.body as string);
    }

    const list: JsonItem[] = [];
    flattenJson(sample.body, [], list);

    return {
        headerNames: Object.keys(sample.headers),
        bodyNames: list.map(row => row.path.join(".")),
        example: sample,
        body: list,
    };
}

interface ListenerHandler {
    url: string;
    formId: string;

}

function WebhookListener({
    onChange,
    formId,
    stepId,
}: {
    onChange: (data: ListenerHandler) => void;
    formId?: string;
    stepId?: string;
}): ReactElement {
    const [generate] = useCreateWebhookUrlMutation();
    const [url, setUrl] = useState<string>();

    const [saveWorkflow] = useSaveWorkflowMutation();

    useEffect(() => {
        const stopper = new Stopper();
        (async () => {
            let currentStepId = stepId;
            if (!currentStepId) {
                let workflow: Workflow;
                if (formId) {
                    const formData = await client.query<GetFormQuery>({
                        query: GetFormDataDocument,
                        variables: {
                            id: formId,
                        },
                    });
                    workflow = formData.data.form?.workflow as Workflow;
                } else {
                    const workflowCreated = await saveWorkflow({
                        variables: {
                            workflow: {
                                name: "Auto Generated Webhook",
                                steps: [createWorklowStep(ACTION_REQUEST_CREATED)],
                            },
                        },
                    });
                    workflow = workflowCreated.data?.saveWorkflow as Workflow;
                }
                const firstStep = workflow?.steps?.find(step => step.actionId === CREATED_ID);
                currentStepId = firstStep?.id ?? `urn:fq:workflow-step:(${workflow?.id},${createId()})`;
            }

            const webhook = await generate({
                variables: {
                    stepId: currentStepId,
                    properties: {
                        isDetached: true,
                        formId,
                    },
                },
            });
            setUrl(webhook.data?.workflowServiceCreateWebhook.url);

            const sampleFormId = await getSampleForm(currentStepId as string, stopper);

            onChange({
                url: url ?? webhook.data?.workflowServiceCreateWebhook.url as string,
                formId: sampleFormId,
            });
        })();

        return () => {
            stopper.isStopped = true;
        };
    }, []);

    return (
        <div className="border p-3 rounded mb-4 bg-white">
            <h4>Listening to Webhook...</h4>
            <p>
                Please use the URL below to test your Webhook by performing a POST with a sample body.
                The body will be used as an example to create an example form.
            </p>
            <CodeBlock code={url} shouldWrap />
            <div className="w-100 justify-content-center d-flex align-items-center mt-2">
                <LoadingIcon size="18px" />
                <div className="text-light mx-2">Waiting for sample webhook call.</div>
            </div>
        </div>
    );
}
WebhookListener.defaultProps = {
    formId: undefined,
    stepId: undefined,
};

export function createFormFields(stepId: string, sample: ParsedBodySample) {
    return [
        {
            id: `urn:fq:workflow-step-group:(${stepId},body)`,
            label: "Webhook Body",
            description: "The webhook body fields.",
            input: sample.bodyNames.map(name => ({
                id: `urn:fq:form-field:${name}`,
                name,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            })),
        },
        {
            id: `urn:fq:workflow-step-group:(${stepId},headers)`,
            label: "Webhook Headers",
            description: "The webhook header fields.",
            input: sample.headerNames.map(name => ({
                id: `urn:fq:form-field:${name}`,
                name,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            })).concat([
                {
                    id: `urn:fq:form-field:ipAddress`,
                    name: "IP Address",
                    type: FormFieldType.Text,
                    isSystemField: true,
                    visibility: FormFieldVisibility.Private,
                },
                {
                    id: `urn:fq:form-field:method`,
                    name: "Method",
                    type: FormFieldType.Text,
                    isSystemField: true,
                    visibility: FormFieldVisibility.Private,
                },
            ]),
        },
        SYSTEM_FIELDS,

    ].filterNull();
}

export function WebhookFormSelected({ id }: {id: string}): ReactElement {
    useArticleContext("urn:fq:article:7c8f62a03cbb41bc8a73612c329879f4");

    const { Paths, historyPush } = useRouting();
    const userSession = useCurrentSession();
    const [isExpandedAutomation, setExpandAutomations] = useState(false);
    const [generate] = useCreateWebhookUrlMutation();

    const [example, setExample] = useState<ParsedBodySample>();
    const [exampleBody, setExampleBody] = useState<JsonItem[]>();

    const isNew = id === "new";
    const {
        data, error, loading: isLoading, refetch,
    } = useGetFormQuery({
        fetchPolicy: "no-cache",
        variables: {
            id: isNew ? "urn:fq:form:new" : id,
            hasId: !isNew,
        },
    });

    useEffect(() => {
        if (data?.form?.exampleInput) {
            const parsed = parse(data?.form?.exampleInput as Example);
            setExample(parsed);
            setExampleBody(parsed.body);
        }
    }, [data?.form?.exampleInput]);

    const [saveForm] = useSaveFormMutation();
    const [saveWorkflow] = useSaveWorkflowMutation();
    const [deleteForm] = useDeleteFormMutation();

    const [name, setName] = useState(() => data?.form?.name ?? "");
    const [description, setDescription] = useState(() => data?.form?.description ?? "");

    const [workflow, setWorkflow] = useState<WorkflowStep[]>(() => data?.form?.workflow?.steps as WorkflowStep[]
     ?? createEmpty());

    const [isReload, setReload] = useState<boolean>();

    const startStep = useMemo(() => workflow?.find(step => step.actionId === CREATED_ID), workflow);
    const [url, setUrl] = useState<string | undefined>();

    useEffect(() => {
        if (!startStep?.id) {
            return;
        }

        (async () => {
            const webhook = await generate({
                variables: {
                    stepId: startStep?.id,
                    properties: {
                        isDetached: true,
                        formId: !isNew ? id : undefined,
                    },
                },
            });
            setUrl(webhook.data?.workflowServiceCreateWebhook.url);
        })();
    }, [startStep?.id]);

    const defaultUserGroups = (isNew ? userSession.user?.userGroups?.toIdList("id") : undefined) ?? [];
    const [userGroupIds, setUserGroupIds] = useState(() => data?.form?.userGroupIds as string[]
            ?? defaultUserGroups);

    const [fields, setFields] = useState<FormFieldDescriptorInput[]>(
        () => data?.form?.fields.map(field => removeTypename(field)) ?? [],
    );

    const [displayDelete, setDisplayDelete] = useState(false);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Finished saving webhook",
        onSave: (event: FormEvent, onSaved) => {
            event.preventDefault();
            (async () => {
                const workflowVersion = !workflow ? undefined : await saveWorkflow({
                    variables: {
                        id: data?.form?.workflow?.id,
                        workflow: {
                            name: "Auto generated",
                            description: "Auto generated for form",
                            steps: workflow.map(createWorklowStep),
                        },
                    },
                });

                await saveForm({
                    refetchQueries: [
                        { query: GetFormDataDocument },
                        { query: ListRequestTypesDocument },
                    ],
                    variables: {
                        id: id !== "new" ? id : undefined,
                        form: {
                            type: FormType.InboundWebhook,
                            fields: fields.map(field => ({
                                ...field,
                                __typename: undefined,
                            })),
                            name,
                            description,
                            userGroupIds,
                            workflowId: workflowVersion?.data?.saveWorkflow?.id,
                        },
                    },
                });
                onSaved();
            })();
        },
    });

    dirtyTracker.useWatchProperty(name, data?.form?.name || "");
    dirtyTracker.useWatchProperty(description, data?.form?.description || "");
    dirtyTracker.useWatchProperty(userGroupIds.join(), (data?.form?.userGroupIds ?? defaultUserGroups).join());

    dirtyTracker.useLoad(() => {
        setName(data?.form?.name ?? "");
        setDescription(data?.form?.description ?? "");
        setFields(data?.form?.fields.map(field => removeTypename(field)) ?? []);
        setUserGroupIds(data?.form?.userGroupIds ?? defaultUserGroups);
        setWorkflow(data?.form?.workflow?.steps as WorkflowStep[] ?? createEmpty());
    }, [data?.form]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    const onDelete = async () => {
        await deleteForm({
            refetchQueries: [
                { query: GetFormDataDocument },
            ],
            variables: {
                id,
            },
        });
        historyPush(Paths.ADMINISTRATOR_SETTING, {
            setting: "workflow-actions",
        });
    };

    return (
        <>
            <Confirm confirmText="Delete" onConfirm={onDelete} shouldShow={displayDelete} onHide={() => setDisplayDelete(false)}>
                <div>
                    Are you sure you want to delete the action:
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {name}
                    </div>

                </div>
            </Confirm>
            <Confirm shouldShow={isReload ?? false} onHide={() => setReload(false)}>
                <WebhookListener
                    formId={isNew ? undefined : id}
                    stepId={startStep?.id}
                    onChange={() => {
                        refetch();
                        setReload(false);
                    }}
                />
            </Confirm>
            <div>
                <div className="mb-4 ">
                    <div className=" border-end me-3 pe-3">
                        <div className="d-flex border p-3 rounded mb-4 bg-white align-items-stretch">
                            <form className="col-7 pe-2">

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bolder">Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Name"
                                        required
                                        onChange={evt => setName(evt.target.value)}
                                        value={name}
                                    />
                                    <Form.Text>
                                        The name of the action displayed at the top and in the action selection.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bolder">
                                        Description
                                        {" "}
                                        <small className="text-light">(Optional)</small>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        as="textarea"
                                        placeholder="This action is used for ..."
                                        onChange={evt => setDescription(evt.target.value)}
                                        value={description}
                                    />
                                    <Form.Text>
                                        The optional description is to help you understand where this action
                                        is used. It will never be displayed to the end user.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bolder">
                                        Groups
                                    </Form.Label>
                                    <UserGroups
                                        isMulti
                                        canCreate
                                        value={userGroupIds}
                                        onSelected={ids => setUserGroupIds(ids)}
                                        canSelectNone
                                    />
                                    <Form.Text>
                                        Which user groups are allowed to edit this action.
                                    </Form.Text>
                                </Form.Group>

                                {dirtyTracker.saveComponent}

                            </form>

                            <div className="mt-2 col-5 ps-2 d-flex flex-column">
                                <div className="flex-grow-1">
                                    <ExpandingSection title="URL" className="p-0">

                                        <CodeBlock
                                            code={url}
                                            shouldWrap
                                            className="mb-3"
                                            codeClassName="text-truncate"
                                        />
                                    </ExpandingSection>

                                    <ExpandingSection title="Headers" className="p-0">
                                        <div className="list-group border-0">
                                            <div className="list-group-item">
                                                <div className="row">
                                                    <div className="col-4 text-truncate">
                                                        Method
                                                    </div>
                                                    <div className="col-8 text-truncate">
                                                        {example?.example?.method}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="list-group-item">
                                                <div className="row">
                                                    <div className="col-4 text-truncate">
                                                        IP Address
                                                    </div>
                                                    <div className="col-8 text-truncate">
                                                        {example?.example?.ipAddress}
                                                    </div>
                                                </div>
                                            </div>
                                            {example?.example?.headers
                                        && Object.entries(example.example.headers).map(([key, value]) => (
                                            <div key={key} className="list-group-item">
                                                <div className="row">
                                                    <div className="col-4 text-truncate">
                                                        {key}
                                                    </div>
                                                    <div className="col-8 text-truncate">
                                                        {value as string}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        </div>
                                    </ExpandingSection>
                                    <ExpandingSection title="Body" defaultExpanded>
                                        <div className="list-group border-0">
                                            {exampleBody?.map(row => (
                                                <div className="row">
                                                    <div className="col-4 text-truncate">
                                                        {row.path.join(".")}
                                                    </div>
                                                    <div className="col-8 text-truncate">
                                                        {row.value as string}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ExpandingSection>
                                </div>
                                {!isNew && (
                                    <PageActions
                                        type="subtle"
                                        onDelete={onDelete}
                                        deleteConfirm={(
                                            <div>
                                                Are you sure you want to delete the webhook:
                                                {" "}
                                                <div className="mt-2 p-2 rounded alert alert-danger">
                                                    {name}
                                                </div>
                                            </div>
                                        )}
                                        buttons={[
                                            {
                                                text: "Refresh",
                                                icon: <BiRefresh />,
                                                onClick: () => {
                                                    setReload(true);
                                                },
                                            },
                                        ]}
                                    />
                                )}

                            </div>
                        </div>

                        <div className="border p-3 rounded mb-4 bg-white">
                            <div className="d-flex justify-content-between w-100 align-items-center mb-2 ">
                                <h5 className="text-light mb-0">
                                    Automations
                                </h5>
                                <button
                                    type="button"
                                    onClick={() => setExpandAutomations(true)}
                                    className="btn btn-outline px-2 py-1 text-light p-1"
                                >
                                    <BiExpandAlt />
                                </button>
                            </div>
                            <div className="mb-5">
                                Automation allows you to create logic, trigger services, and take actions on this requests.
                            </div>

                            <WorkflowDisplay
                                value={workflow}
                                onChange={updated => {
                                    setWorkflow(updated);
                                    dirtyTracker.dirty();
                                }}
                                input={example ? createFormFields(startStep?.id, example) : undefined}
                                isModal={isExpandedAutomation}
                                type={ActionContextType.Webhook}
                                onHideModal={() => setExpandAutomations(false)}
                            />

                        </div>

                    </div>

                </div>

            </div>
        </>
    );
}

export default function WebhookForm(): ReactElement {
    const { Paths, historyPush } = useRouting();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    if (id === "new") {
        return (
            <WebhookListener onChange={data => {
                historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                    setting: "workflow-webhooks",
                    id: data.formId,
                });
            }}
            />
        );
    }
    return id ? <WebhookFormSelected id={id} /> : <>No workflow found</>;
}
